import type { Candle } from "./types";
import type { DeepPartial, HandleScaleOptions, HandleScrollOptions, PriceScaleOptions, UTCTimestamp } from "lightweight-charts";

export type TimeframeLabel = "M1" | "M5" | "M15" | "M30" | "H1" | "H4" | "D1" | "W1" | "MN";
export type ChartType = "Candles" | "Bars" | "Line" | "Area" | "Heikin Ashi";
export type CandleUpdateMode = "full" | "tail";
export type ChartDrawingTool = "measure" | "risk-reward" | "trendline" | "horizontal" | "vertical" | "fibonacci" | "rectangle" | "brush" | "arrow";

export interface ChartDrawingPoint {
  x: number;
  y: number;
}

export interface ChartAnnotation {
  id: string;
  tool: ChartDrawingTool;
  start: ChartDrawingPoint;
  end?: ChartDrawingPoint;
  createdAt: number;
}

export const chartDrawingTools: readonly ChartDrawingTool[] = ["measure", "risk-reward", "trendline", "horizontal", "vertical", "fibonacci", "rectangle", "brush", "arrow"];

export function drawingToolNeedsEnd(tool: ChartDrawingTool): boolean {
  return !["horizontal", "vertical"].includes(tool);
}

export function chartDrawingStorageKey(symbol: string | null | undefined, timeframe: TimeframeLabel): string {
  return `${String(symbol || "market").toUpperCase()}:${timeframe}`;
}

export function normalizeChartAnnotations(value: unknown, maxItems = 80): ChartAnnotation[] {
  if (!Array.isArray(value)) return [];
  const output: ChartAnnotation[] = [];
  for (const item of value) {
    if (!item || typeof item !== "object") continue;
    const candidate = item as Partial<ChartAnnotation>;
    if (!candidate.id || typeof candidate.id !== "string" || !chartDrawingTools.includes(candidate.tool as ChartDrawingTool)) continue;
    const point = (value: unknown): ChartDrawingPoint | null => {
      if (!value || typeof value !== "object") return null;
      const source = value as Partial<ChartDrawingPoint>;
      const x = Number(source.x);
      const y = Number(source.y);
      if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
      return { x: Math.max(0, Math.min(1, x)), y: Math.max(0, Math.min(1, y)) };
    };
    const start = point(candidate.start);
    const end = candidate.end === undefined ? undefined : point(candidate.end);
    if (!start || candidate.end !== undefined && !end) continue;
    output.push({
      id: candidate.id.slice(0, 80),
      tool: candidate.tool as ChartDrawingTool,
      start,
      ...(end ? { end } : {}),
      createdAt: Number.isFinite(Number(candidate.createdAt)) ? Number(candidate.createdAt) : 0
    });
  }
  return output.slice(-maxItems);
}

export const timeframeOptions: Array<{ label: TimeframeLabel; interval: string; initialLimit: number }> = [
  { label: "M1", interval: "1m", initialLimit: 500 },
  { label: "M5", interval: "5m", initialLimit: 500 },
  { label: "M15", interval: "15m", initialLimit: 500 },
  { label: "M30", interval: "30m", initialLimit: 500 },
  { label: "H1", interval: "1h", initialLimit: 500 },
  { label: "H4", interval: "4h", initialLimit: 400 },
  { label: "D1", interval: "1d", initialLimit: 365 },
  { label: "W1", interval: "1w", initialLimit: 260 },
  { label: "MN", interval: "1M", initialLimit: 240 }
];

export const chartTypes: ChartType[] = ["Candles", "Bars", "Line", "Area", "Heikin Ashi"];
export const defaultIndicators = ["volume", "ema20", "ema50", "ema200"];
export const mainPricePaneIndex = 0;
export const volumePaneIndex = 1;
export const maxPriceLineDeviation = 0.15;

export const chartHandleScaleOptions: DeepPartial<HandleScaleOptions> = {
  axisPressedMouseMove: { time: true, price: false },
  axisDoubleClickReset: { time: true, price: true },
  mouseWheel: true,
  pinch: true
};

export const chartHandleScrollOptions: DeepPartial<HandleScrollOptions> = {
  mouseWheel: true,
  pressedMouseMove: true,
  horzTouchDrag: true,
  vertTouchDrag: false
};

export const mainPriceScaleOptions: DeepPartial<PriceScaleOptions> = {
  autoScale: true,
  scaleMargins: { top: 0.1, bottom: 0.1 }
};

export function toChartTime(timestamp: number): UTCTimestamp {
  if (!Number.isFinite(timestamp) || timestamp <= 0) throw new Error(`Invalid chart timestamp: ${timestamp}`);
  return Math.floor((timestamp > 10_000_000_000 ? timestamp : timestamp * 1000) / 1000) as UTCTimestamp;
}

export function normalizeChartCandles(candles: Candle[]): Candle[] {
  const normalized = candles
    .map((candle) => ({
      openTime: Number(candle.openTime),
      open: Number(candle.open),
      high: Number(candle.high),
      low: Number(candle.low),
      close: Number(candle.close),
      volume: Number(candle.volume),
      closeTime: Number(candle.closeTime)
    }))
    .filter((candle) => {
      const validNumbers = [candle.openTime, candle.open, candle.high, candle.low, candle.close, candle.volume, candle.closeTime].every(Number.isFinite);
      if (!validNumbers) return false;
      if (candle.open <= 0 || candle.high <= 0 || candle.low <= 0 || candle.close <= 0 || candle.volume < 0) return false;
      if (candle.high < candle.open || candle.high < candle.close || candle.high < candle.low) return false;
      if (candle.low > candle.open || candle.low > candle.close) return false;
      const range = candle.high - candle.low;
      const midpoint = (candle.high + candle.low) / 2;
      return midpoint > 0 && range / midpoint < 0.8;
    });
  return dedupeCandles(normalized);
}

export function chartPricePrecision(price: number | null | undefined): { precision: number; minMove: number } {
  const absolute = Math.abs(price || 0);
  const precision = absolute >= 100 ? 2 : absolute >= 1 ? 4 : absolute >= 0.1 ? 5 : absolute >= 0.01 ? 6 : 8;
  return { precision, minMove: Number((10 ** -precision).toFixed(precision)) };
}

export function toOhlcSeries(candles: Candle[]) {
  return normalizeChartCandles(candles).map((candle) => ({
    time: toChartTime(candle.openTime),
    open: candle.open,
    high: candle.high,
    low: candle.low,
    close: candle.close
  }));
}

export function toCloseSeries(candles: Candle[]) {
  return normalizeChartCandles(candles).map((candle) => ({ time: toChartTime(candle.openTime), value: candle.close }));
}

export function toVolumeSeries(candles: Candle[]) {
  return normalizeChartCandles(candles).map((candle) => ({
    time: toChartTime(candle.openTime),
    value: candle.volume,
    color: candle.close >= candle.open ? "rgba(74,222,128,0.28)" : "rgba(248,113,113,0.28)"
  }));
}

export function timeframeLabelFromInterval(interval: string): TimeframeLabel {
  return timeframeOptions.find((item) => item.interval === interval)?.label || "H1";
}

export function intervalFromTimeframe(label: TimeframeLabel): string {
  return timeframeOptions.find((item) => item.label === label)?.interval || "1h";
}

export function initialLimitForTimeframe(label: TimeframeLabel): number {
  return timeframeOptions.find((item) => item.label === label)?.initialLimit || 500;
}

export function pollingMsForTimeframe(label: TimeframeLabel): number {
  if (label === "M1") return 8000;
  if (["M5", "M15", "M30"].includes(label)) return 15000;
  if (["H1", "H4"].includes(label)) return 30000;
  return 60000;
}

export function dedupeCandles(candles: Candle[]): Candle[] {
  const byTime = new Map<number, Candle>();
  for (const candle of candles) byTime.set(candle.openTime, candle);
  return [...byTime.values()].sort((a, b) => a.openTime - b.openTime);
}

export function mergeCandles(existing: Candle[], incoming: Candle[]): Candle[] {
  return dedupeCandles([...existing, ...incoming]);
}

export function liveUpdateCandles(existing: Candle[], incoming: Candle[]): { candles: Candle[]; changed: Candle[] } {
  const before = new Map(normalizeChartCandles(existing).map((candle) => [candle.openTime, candle]));
  const merged = mergeCandles(existing, incoming);
  const changed = merged.filter((candle) => {
    const previous = before.get(candle.openTime);
    return !previous
      || previous.open !== candle.open
      || previous.high !== candle.high
      || previous.low !== candle.low
      || previous.close !== candle.close
      || previous.volume !== candle.volume;
  });
  return { candles: merged, changed };
}

export function shouldFitChart(updateMode: CandleUpdateMode, identityChanged: boolean, userRequested = false): boolean {
  return userRequested || identityChanged || updateMode === "full" && identityChanged;
}

export function isPlausiblePriceLine(linePrice: number | null | undefined, marketPrice: number | null | undefined, maxDeviation = maxPriceLineDeviation): boolean {
  if (!Number.isFinite(linePrice) || !Number.isFinite(marketPrice)) return false;
  if (!linePrice || !marketPrice || linePrice <= 0 || marketPrice <= 0) return false;
  return Math.abs(linePrice - marketPrice) / marketPrice <= maxDeviation;
}

export interface SeriesDiagnostic {
  name: string;
  priceScaleId: string;
  count: number;
  min: number | null;
  max: number | null;
  firstTime: number | null;
  lastTime: number | null;
  symbol: string | null;
  timeframe: TimeframeLabel;
  affectsAutoScale: boolean;
}

export function seriesDiagnostic(name: string, priceScaleId: string, values: Array<{ time: UTCTimestamp; value?: number; open?: number; high?: number; low?: number; close?: number }>, symbol: string | null, timeframe: TimeframeLabel, affectsAutoScale: boolean): SeriesDiagnostic {
  const prices = values.flatMap((item) => [item.value, item.open, item.high, item.low, item.close]).filter((value): value is number => Number.isFinite(value));
  return {
    name,
    priceScaleId,
    count: values.length,
    min: prices.length ? Math.min(...prices) : null,
    max: prices.length ? Math.max(...prices) : null,
    firstTime: values[0]?.time ? Number(values[0].time) : null,
    lastTime: values.at(-1)?.time ? Number(values.at(-1)?.time) : null,
    symbol,
    timeframe,
    affectsAutoScale
  };
}

export function heikinAshi(candles: Candle[]): Candle[] {
  const output: Candle[] = [];
  for (const candle of candles) {
    const close = (candle.open + candle.high + candle.low + candle.close) / 4;
    const previous = output.at(-1);
    const open = previous ? (previous.open + previous.close) / 2 : (candle.open + candle.close) / 2;
    output.push({
      openTime: candle.openTime,
      open,
      high: Math.max(candle.high, open, close),
      low: Math.min(candle.low, open, close),
      close,
      volume: candle.volume,
      closeTime: candle.closeTime
    });
  }
  return output;
}

export function formatPrice(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "-";
  const absolute = Math.abs(value);
  const fractionDigits = chartPricePrecision(absolute).precision;
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: Math.min(2, fractionDigits),
    maximumFractionDigits: fractionDigits
  }).format(value);
}

export function candleStats(candles: Candle[]) {
  const last = candles.at(-1);
  const dayWindow = candles.slice(-24);
  const sessionWindow = candles.slice(-96);
  return {
    last,
    high24: dayWindow.length ? Math.max(...dayWindow.map((candle) => candle.high)) : null,
    low24: dayWindow.length ? Math.min(...dayWindow.map((candle) => candle.low)) : null,
    volume24: dayWindow.length ? dayWindow.reduce((sum, candle) => sum + candle.volume, 0) : null,
    sessionHigh: sessionWindow.length ? Math.max(...sessionWindow.map((candle) => candle.high)) : null,
    sessionLow: sessionWindow.length ? Math.min(...sessionWindow.map((candle) => candle.low)) : null
  };
}
